/**
 * /orgs/:orgId/collections/migrate?path= — repairing one legacy collection
 * path, as a three-step wizard (Feature 70, D5: a form with a preview before
 * commit is a page).
 *
 * It cannot be one click: the repair assigns an audience, and an audience
 * defaulted on the operator's behalf is exactly the silent grant Feature 36
 * exists to prevent. Feature 31 requires the mapping to be stated and
 * previewed, because it grants real access to real documents and cannot be
 * undone from the previous state. So: Choose states the target and the
 * groups, Preview shows the plan for that exact body, Migrate submits it.
 * Going back to Choose throws the plan away rather than leaving a stale one
 * next to a Migrate button.
 *
 * `path=` with no value is the real key for documents that named no
 * collection at all; without it the documents most in need of repair would be
 * the only ones the repair could not reach.
 */
import { listAgents } from '@optum/api-client/endpoints/agents';
import {
  previewLegacyMigration,
  runLegacyMigration,
} from '@optum/api-client/endpoints/document-migrations';
import { listGroups } from '@optum/api-client/endpoints/groups';
import type {
  LegacyMigrationAccepted,
  LegacyMigrationBody,
  LegacyMigrationPlan,
} from '@optum/api-client/types';
import { ApiError } from '@optum/api-client/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { formatRecordParam, RECORD_PARAM } from '../../components/shell/record.js';
import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Field } from '../../ui/Field.js';
import { Badge, FilterChip, Input, Typography, WizardLayout } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { useUnsavedChanges } from '../../lib/unsaved.js';

const STEPS = [
  { label: 'Choose', sub: 'target and audience' },
  { label: 'Preview', sub: 'what will move' },
  { label: 'Migrate', sub: 'submitted' },
];

export function MigratePage() {
  const orgId = useRequiredOrgId();
  const [params] = useSearchParams();
  const legacyTag = params.get('path');
  useBreadcrumbTail('Migrate');
  if (legacyTag === null) {
    return <ErrorBlock message="No path named. Open the migration from a row on Collections." />;
  }
  return <MigrateWizard orgId={orgId} legacyTag={legacyTag} />;
}

function MigrateWizard({ orgId, legacyTag }: { orgId: string; legacyTag: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const unfiled = legacyTag === '';
  const [step, setStep] = useState(0);
  const [target, setTarget] = useState(legacyTag);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [chosenAgent, setChosenAgent] = useState<string | null>(null);
  const [plan, setPlan] = useState<LegacyMigrationPlan | null>(null);
  const [accepted, setAccepted] = useState<LegacyMigrationAccepted | null>(null);

  const groups = useQuery({ queryKey: qk.groups(orgId), queryFn: () => listGroups(orgId) });
  const agents = useQuery({
    queryKey: qk.agents(orgId),
    queryFn: () => listAgents({ organization_id: orgId }),
  });
  // Any agent of the tenant can carry the repair tasks, so the first one is a
  // reasonable default — but it stays visible and changeable, because the task
  // stream will show it as the submitter.
  const agentId = chosenAgent ?? agents.data?.[0]?.id ?? '';

  const body: LegacyMigrationBody = {
    agent_id: agentId,
    mapping: { [legacyTag]: { collections: target.trim(), allowed_groups: groupIds } },
  };
  const ready = agentId !== '' && target.trim() !== '' && groupIds.length > 0;
  const release = useUnsavedChanges(
    accepted === null && (groupIds.length > 0 || target !== legacyTag),
  );

  const back = () => {
    const params = new URLSearchParams(
      legacyTag
        ? { [RECORD_PARAM]: formatRecordParam({ kind: 'collection', id: legacyTag, orgId }) }
        : {},
    );
    const qs = params.toString();
    navigate(`/orgs/${orgId}/collections${qs ? `?${qs}` : ''}`);
  };

  const preview = useMutation({
    mutationFn: () => previewLegacyMigration(body),
    onSuccess: (p) => {
      setPlan(p);
      setStep(1);
    },
    onError: (e) => toast.error('Preview failed', e instanceof ApiError ? e.message : String(e)),
  });

  const run = useMutation({
    mutationFn: () => runLegacyMigration(body),
    onSuccess: (out) => {
      setAccepted(out);
      setStep(2);
      qc.invalidateQueries({ queryKey: qk.collections(orgId) });
      toast.success(
        `Migrating ${out.submitted.length} document${out.submitted.length === 1 ? '' : 's'}`,
        out.failed.length > 0 ? `${out.failed.length} could not be submitted.` : undefined,
      );
    },
    onError: (e) => toast.error('Migration failed', e instanceof ApiError ? e.message : String(e)),
  });

  function toggleGroup(id: string) {
    setGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  }

  function next() {
    if (step === 0) preview.mutate();
    else if (step === 1) run.mutate();
    else {
      release();
      back();
    }
  }

  const planned = plan?.planned.length ?? 0;
  const nextLabel =
    step === 0
      ? preview.isPending
        ? 'Checking…'
        : 'Preview'
      : step === 1
        ? run.isPending
          ? 'Submitting…'
          : `Migrate ${planned} document${planned === 1 ? '' : 's'}`
        : 'Back to Collections';
  const nextDisabled =
    step === 0 ? !ready || preview.isPending : step === 1 ? planned === 0 || run.isPending : false;

  return (
    <WizardLayout
      steps={STEPS}
      current={step}
      icon="drive_file_move"
      heading={unfiled ? 'Migrate documents with no collection' : `Migrate ${legacyTag}`}
      headingSubtitle="These documents carry no audience, so retrieval cannot see them. Repair assigns a collection and the groups allowed to read it. It cannot be undone from the previous state."
      {...(step === 1
        ? {
            onBack: () => {
              setPlan(null);
              setStep(0);
            },
          }
        : {})}
      onNext={next}
      nextLabel={nextLabel}
      nextDisabled={nextDisabled}
      onDiscard={back}
      discardLabel={step === 2 ? 'Close' : 'Cancel'}
      maxWidth={760}
    >
      {step === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            id="mig-target"
            label="Target collection"
            required
            hint={
              unfiled
                ? 'These documents name no collection, so there is nothing to default to.'
                : 'Where these documents will live. Defaults to the path they already report.'
            }
          >
            <Input
              id="mig-target"
              className="mono"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </Field>
          <div>
            <Typography variant="eyebrow" style={{ marginBottom: 7, display: 'block' }}>
              Audience
            </Typography>
            {groups.isPending && <LoadingBlock />}
            {groups.isError && (
              <Typography variant="meta" style={{ color: 'var(--danger)' }}>
                Could not load this tenant&rsquo;s groups, so there is nothing to grant to.
              </Typography>
            )}
            {groups.isSuccess && groups.data.length === 0 && (
              <Typography variant="meta" style={{ color: 'var(--warn)' }}>
                This tenant has no groups. Create one first — a document granted to nobody stays
                unreachable.
              </Typography>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {(groups.data ?? []).map((g) => (
                <FilterChip
                  key={g.id}
                  label={g.name}
                  active={groupIds.includes(g.id)}
                  onClick={() => toggleGroup(g.id)}
                />
              ))}
            </div>
            <Typography variant="meta" style={{ marginTop: 7, display: 'block' }}>
              Only members of the chosen groups will be able to retrieve them.
            </Typography>
          </div>
          <Field id="mig-agent" label="Submitting agent" required>
            <Select
              searchable
              id="mig-agent"
              value={agentId}
              onChange={(value) => setChosenAgent(value)}
              options={(agents.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
            />
          </Field>
        </div>
      ) : step === 1 && plan ? (
        <PlanSummary plan={plan} />
      ) : accepted ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <Badge tone="ok">{`${accepted.submitted.length} submitted`}</Badge>
            {accepted.failed.length > 0 ? (
              <Badge tone="danger">{`${accepted.failed.length} failed`}</Badge>
            ) : null}
          </div>
          <Typography variant="body-muted">
            The repair runs as tasks under the submitting agent; the path&rsquo;s status changes as
            they complete.
          </Typography>
        </div>
      ) : null}
    </WizardLayout>
  );
}

function PlanSummary({ plan }: { plan: LegacyMigrationPlan }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
        <Badge
          tone={plan.planned.length > 0 ? 'ok' : 'neutral'}
        >{`${plan.planned.length} to migrate`}</Badge>
        {plan.skipped.length > 0 && <Badge tone="warn">{`${plan.skipped.length} skipped`}</Badge>}
        {plan.remaining > 0 && (
          <Badge tone="neutral">{`${plan.remaining} beyond this batch`}</Badge>
        )}
      </div>
      {plan.planned.length === 0 && (
        <Typography variant="body-muted">
          Nothing matches this mapping. The path must be the tag these documents were ingested with.
          Go back and change it.
        </Typography>
      )}
      {plan.records.length > 0 && <Typography variant="meta">{plan.records_note}</Typography>}
    </div>
  );
}
